import {
  type ReactNode,
  useRef,
  Suspense,
  useMemo,
  useState,
  useEffect,
} from 'react';
import {Listbox} from '@headlessui/react';
import {defer, type LoaderArgs} from '@shopify/remix-oxygen';
import {
  useLoaderData,
  Await,
  useSearchParams,
  useLocation,
  useNavigation,
} from '@remix-run/react';

import {
  AnalyticsPageType,
  Money,
  ShopifyAnalyticsProduct,
  ShopPayButton,
} from '@shopify/hydrogen';
import {
  Heading,
  IconCaret,
  IconCheck,
  ProductGallery,
  ProductSwimlane,
  Skeleton,
  Text,
  Link,
  AddToCartButton,
} from '~/components';
import {getExcerpt, renderMarkDown} from '~/lib/utils';
import {seoPayload} from '~/lib/seo.server';
import invariant from 'tiny-invariant';
import clsx from 'clsx';
import type {
  SelectedOptionInput,
  Product as ProductType,
  ProductConnection,
} from '@shopify/hydrogen/storefront-api-types';
import {
  COLLECTION_CONTENT_QUERY,
  metaObjectContentFragment,
} from '~/data/fragments';
import type {Storefront} from '~/lib/type';
import type {Product} from 'schema-dts';
import {routeHeaders, CACHE_SHORT} from '~/data/cache';
import * as _ from 'lodash';
import {getProductDetailImages, getSiteDetails} from '~/utils';
import {useGetContext} from '~/hooks/useGetContext';
import NavbarSection from '~/components/Sections/NavbarSection';
import {Tab, Tabs, TabList, TabPanel} from 'react-tabs';
import DataSheet from '~/components/Product/DataSheet';
import storefrontFetcher, {fetchSite} from '~/lib/storefrontFetcher';
import {GeneratePdf} from '~/components/Pdf/pdf';
export const headers = routeHeaders;

export async function loader({params, request, context}: LoaderArgs) {
  const {productHandle} = params;
  invariant(productHandle, 'Missing productHandle param, check route filename');

  const searchParams = new URL(request.url).searchParams;

  const selectedOptions: SelectedOptionInput[] = [];
  searchParams.forEach((value, name) => {
    selectedOptions.push({name, value});
  });
  const {shop, product} = await storefrontFetcher({
    context,
    query: PRODUCT_QUERY,
    variables: {
      handle: productHandle,
      selectedOptions,
      country: context.storefront.i18n.country,
      language: context.storefront.i18n.language,
    },
  });
  if (!product?.id) {
    throw new Response('product', {status: 404});
  }

  const properties = _.get(
    JSON.parse(_.get(product, 'properties[0].value', '{}')),
    'data',
    [],
  );

  const items = _.get(
    JSON.parse(_.get(product, 'items[0].value', '{}')),
    'data',
    [],
  );

  const ids: string[] = [];
  items.map((element) => {
    ids.push(_.get(element, 'item.id'));
  });

  console.log('Items ids: ', ids);

  properties.map((element) => {
    ids.push(_.get(element, 'propertyBase.id'));
  });

  // validate product handle
  const site = await fetchSite({context});
  const {product_collections} = await getSiteDetails(context, site);
  let found = false;
  let recommendedCollection = '';
  const collections = product_collections.map(
    (collection: any) => collection?.id,
  );
  _.map(collections, (it) => {
    if (
      product?.collections?.nodes
        ?.map((_it: any) => _.get(_it, 'id'))
        .includes(it)
    ) {
      found = true;
      recommendedCollection = it;
    }
  });
  if (!found) {
    throw new Response(null, {
      status: 404,
      statusText: 'Not Found',
    });
  }

  const recommended = getRecommendedProducts(
    context.storefront,
    recommendedCollection,
    product.id,
  );
  const firstVariant = product.variants.nodes[0];
  const selectedVariant = product.selectedVariant ?? firstVariant;

  const productAnalytics: ShopifyAnalyticsProduct = {
    productGid: product.id,
    variantGid: selectedVariant.id,
    name: product.title,
    variantName: selectedVariant.title,
    brand: product.vendor,
    price: selectedVariant.price.amount,
  };

  const image = _.get(product, 'mainMedia[0].value', '');

  const seo = seoPayload.product({
    product,
    selectedVariant,
    url: request.url,
    image,
  });
  return defer(
    {
      product,
      shop,
      storeDomain: shop.primaryDomain.url,
      recommended,
      analytics: {
        pageType: AnalyticsPageType.product,
        resourceId: product.id,
        products: [productAnalytics],
        totalValue: parseFloat(selectedVariant.price.amount),
      },
      seo,
      metaobjectIds: ids,
      items,
      properties,
    },
    {
      headers: {
        'Cache-Control': CACHE_SHORT,
      },
    },
  );
}

export default function Product() {
  const {product, recommended, metaobjectIds, properties, items} =
    useLoaderData<typeof loader>();
  const {menu, logo, isAuthenticated} = useGetContext();
  const productProperties = _.get(product, 'productproperty');
  const productBom = _.get(product, 'productBom.references.nodes', '');

  let images: any = [];
  const metadataImages = getProductDetailImages(product);
  if (!_.isEmpty(metadataImages)) {
    images = metadataImages;
  }

  console.log('Product Items: ', items);

  return (
    <>
      <NavbarSection
        menu={menu}
        logo={logo || ''}
        title={'Products' || ''}
        isAuthenticated={isAuthenticated}
      />
      <div className="shop-details-area ptb-80">
        <div className="container">
          <div className="row">
            <div className="col-lg-5">
              <ProductGallery media={images} className="w-full lg:col-span-2" />
            </div>
            <div className="col-lg-7">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginBottom: '-2.5rem',
                }}
              >
                <GeneratePdf
                  metaobjectIds={metaobjectIds}
                  images={images}
                  product={product}
                  items={items}
                  properties={properties}
                />
              </div>
              <div className="products-details-tabs">
                <Tabs>
                  <TabList>
                    <Tab>Description</Tab>
                    <Tab>Datasheet</Tab>
                  </TabList>

                  <TabPanel>
                    <div className="products-details">
                      <h3>{product && product.title}</h3>
                      <ProductForm />
                    </div>
                  </TabPanel>
                  <TabPanel>
                    <DataSheet
                      productBom={productBom}
                      productProperties={productProperties}
                      metaobjectIds={metaobjectIds}
                      items={items}
                      properties={properties}
                    />
                  </TabPanel>
                </Tabs>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="related-products">
        <Suspense fallback={<Skeleton className="h-32" />}>
          <Await
            errorElement="There was a problem loading related products"
            resolve={recommended}
          >
            {(products) => (
              <ProductSwimlane title="Related Products" products={products} />
            )}
          </Await>
        </Suspense>
      </div>
    </>
  );
}

export function ProductForm() {
  const {product, shop, analytics, storeDomain} =
    useLoaderData<typeof loader>();
  const {descriptionHtml, productdescription = ''} = product;
  const {shippingPolicy, refundPolicy} = shop;
  const [currentSearchParams] = useSearchParams();
  const {location} = useNavigation();
  function parseJSON(str: string | undefined) {
    if (str) {
      try {
        return JSON.parse(str);
      } catch (error) {
        return {};
      }
    }
    return {};
  }
  const description = parseJSON(_.get(productdescription, '[0].value'));
  /**
   * We update `searchParams` with in-flight request data from `location` (if available)
   * to create an optimistic UI, e.g. check the product option before the
   * request has completed.
   */
  const searchParams = useMemo(() => {
    return location
      ? new URLSearchParams(location.search)
      : currentSearchParams;
  }, [currentSearchParams, location]);

  const firstVariant = product.variants.nodes[0];

  /**
   * We're making an explicit choice here to display the product options
   * UI with a default variant, rather than wait for the user to select
   * options first. Developers are welcome to opt-out of this behavior.
   * By default, the first variant's options are used.
   */
  const searchParamsWithDefaults = useMemo<URLSearchParams>(() => {
    const clonedParams = new URLSearchParams(searchParams);

    for (const {name, value} of firstVariant.selectedOptions) {
      if (!searchParams.has(name)) {
        clonedParams.set(name, value);
      }
    }

    return clonedParams;
  }, [searchParams, firstVariant.selectedOptions]);

  /**
   * Likewise, we're defaulting to the first variant for purposes
   * of add to cart if there is none returned from the loader.
   * A developer can opt out of this, too.
   */
  const selectedVariant = product.selectedVariant ?? firstVariant;
  const isOutOfStock = !selectedVariant?.availableForSale;

  const isOnSale =
    selectedVariant?.price?.amount &&
    selectedVariant?.compareAtPrice?.amount &&
    selectedVariant?.price?.amount < selectedVariant?.compareAtPrice?.amount;

  const productAnalytics: ShopifyAnalyticsProduct = {
    ...analytics.products[0],
    quantity: 1,
  };

  return (
    <>
      <div className="grid gap-10">
        <div className="grid gap-4">
          <ProductOptions
            options={product.options}
            searchParamsWithDefaults={searchParamsWithDefaults}
          />
          {selectedVariant && (
            <div className="grid items-stretch gap-4">
              <div className="grid gap-4">
                {description && (
                  <ProductDetail
                    title="Product Details"
                    content={_.get(description, 'data') || descriptionHtml}
                  />
                )}
                {shippingPolicy?.body && (
                  <ProductDetail
                    title="Shipping"
                    content={getExcerpt(shippingPolicy.body)}
                    learnMore={`/policies/${shippingPolicy.handle}`}
                  />
                )}
                {refundPolicy?.body && (
                  <ProductDetail
                    title="Returns"
                    content={getExcerpt(refundPolicy.body)}
                    learnMore={`/policies/${refundPolicy.handle}`}
                  />
                )}
              </div>

              {/* <div className="row">
                <div className="col-3">
                  {isOutOfStock ? null : (
                    <AddToCartButton
                      lines={[
                        {
                          merchandiseId: selectedVariant.id,
                          quantity: 1,
                        },
                      ]}
                      variant="primary"
                      data-test="add-to-cart"
                      analytics={{
                        products: [productAnalytics],
                        totalValue: parseFloat(productAnalytics.price),
                      }}
                    >
                      <Text
                        as="span"
                        className="flex items-center justify-center gap-2"
                      >
                        <span>Add to Bag</span>
                      </Text>
                    </AddToCartButton>
                  )}
                </div>
                <div className="col-3">
                  {!isOutOfStock && (
                    <ShopPayButton
                      width="100%"
                      variantIds={[selectedVariant?.id!]}
                      storeDomain={storeDomain}
                    />
                  )}
                </div>
              </div> */}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function ProductOptions({
  options,
  searchParamsWithDefaults,
}: {
  options: ProductType['options'];
  searchParamsWithDefaults: URLSearchParams;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  return (
    <>
      {options
        .filter((option) => option.values.length > 1)
        .map((option) => (
          <div
            key={option.name}
            className="flex flex-col flex-wrap mb-4 gap-y-2 last:mb-0"
          >
            <Heading as="legend" size="lead" className="min-w-[4rem]">
              {option.name}
            </Heading>
            <div className="flex flex-wrap items-baseline gap-4">
              {/**
               * First, we render a bunch of <Link> elements for each option value.
               * When the user clicks one of these buttons, it will hit the loader
               * to get the new data.
               *
               * If there are more than 7 values, we render a dropdown.
               * Otherwise, we just render plain links.
               */}
              {option.values.length > 7 ? (
                <div className="relative w-full">
                  <Listbox>
                    {({open}) => (
                      <>
                        <Listbox.Button
                          ref={closeRef}
                          className={clsx(
                            'flex items-center justify-between w-full py-3 px-4 border border-primary',
                            open
                              ? 'rounded-b md:rounded-t md:rounded-b-none'
                              : 'rounded',
                          )}
                        >
                          <span>
                            {searchParamsWithDefaults.get(option.name)}
                          </span>
                          <IconCaret direction={open ? 'up' : 'down'} />
                        </Listbox.Button>
                        <Listbox.Options
                          className={clsx(
                            'border-primary bg-contrast absolute bottom-12 z-30 grid h-48 w-full overflow-y-scroll rounded-t border px-2 py-2 transition-[max-height] duration-150 sm:bottom-auto md:rounded-b md:rounded-t-none md:border-t-0 md:border-b',
                            open ? 'max-h-48' : 'max-h-0',
                          )}
                        >
                          {option.values.map((value) => (
                            <Listbox.Option
                              key={`option-${option.name}-${value}`}
                              value={value}
                            >
                              {({active}) => (
                                <ProductOptionLink
                                  optionName={option.name}
                                  optionValue={value}
                                  className={clsx(
                                    'text-primary w-full p-2 transition rounded flex justify-start items-center text-left cursor-pointer',
                                    active && 'bg-primary/10',
                                  )}
                                  searchParams={searchParamsWithDefaults}
                                  onClick={() => {
                                    if (!closeRef?.current) return;
                                    closeRef.current.click();
                                  }}
                                >
                                  {value}
                                  {searchParamsWithDefaults.get(option.name) ===
                                    value && (
                                    <span className="ml-2">
                                      <IconCheck />
                                    </span>
                                  )}
                                </ProductOptionLink>
                              )}
                            </Listbox.Option>
                          ))}
                        </Listbox.Options>
                      </>
                    )}
                  </Listbox>
                </div>
              ) : (
                <>
                  {option.values.map((value) => {
                    const checked =
                      searchParamsWithDefaults.get(option.name) === value;
                    const id = `option-${option.name}-${value}`;

                    return (
                      <Text key={id}>
                        <ProductOptionLink
                          optionName={option.name}
                          optionValue={value}
                          searchParams={searchParamsWithDefaults}
                          className={clsx(
                            'leading-none py-1 border-b-[1.5px] cursor-pointer transition-all duration-200',
                            checked ? 'border-primary/50' : 'border-primary/0',
                          )}
                        />
                      </Text>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        ))}
    </>
  );
}

function ProductOptionLink({
  optionName,
  optionValue,
  searchParams,
  children,
  ...props
}: {
  optionName: string;
  optionValue: string;
  searchParams: URLSearchParams;
  children?: ReactNode;
  [key: string]: any;
}) {
  const {pathname} = useLocation();
  const isLangPathname = /\/[a-zA-Z]{2}-[a-zA-Z]{2}\//g.test(pathname);
  // fixes internalized pathname
  const path = isLangPathname
    ? `/${pathname.split('/').slice(2).join('/')}`
    : pathname;

  const clonedSearchParams = new URLSearchParams(searchParams);
  clonedSearchParams.set(optionName, optionValue);

  return (
    <Link
      {...props}
      preventScrollReset
      prefetch="intent"
      replace
      to={`${path}?${clonedSearchParams.toString()}`}
    >
      {children ?? optionValue}
    </Link>
  );
}

function ProductDetail({
  title,
  content,
  learnMore,
}: {
  title: string;
  content: string;
  learnMore?: string;
}) {
  const [domLoaded, setDomLoaded] = useState(false);

  useEffect(() => {
    setDomLoaded(true);
  }, []);
  if (!domLoaded) {
    return null;
  }
  return (
    <>
      <span>{title}</span>
      {/<[a-z/][\s\S]*>/i.test(content) ? (
        <p
          className="prose dark:prose-invert"
          dangerouslySetInnerHTML={{__html: content}}
        />
      ) : (
        <p
          className="prose dark:prose-invert"
          dangerouslySetInnerHTML={{
            __html: renderMarkDown(content),
          }}
        />
      )}
    </>
  );
}

const PRODUCT_VARIANT_FRAGMENT = `
#graphql
  fragment ProductVariantFragment on ProductVariant {
    id
    availableForSale
    selectedOptions {
      name
      value
    }
    image {
      id
      url
      altText
      width
      height
    }
    price {
      amount
      currencyCode
    }
    compareAtPrice {
      amount
      currencyCode
    }
    sku
    title
    unitPrice {
      amount
      currencyCode
    }
    product {
      title
      handle
    }
  }
`;

const PRODUCT_QUERY = `#graphql${PRODUCT_VARIANT_FRAGMENT} ${metaObjectContentFragment}
  query Product(
    $country: CountryCode
    $language: LanguageCode
    $handle: String!
    $selectedOptions: [SelectedOptionInput!]!
  ) @inContext(country: $country, language: $language) {
    product(handle: $handle) {
      id
      title
      vendor
      handle
      descriptionHtml
      description
      options {
        name
        values
      }
      collections(first:100) {
        nodes {
          id
        }
      }
      selectedVariant: variantBySelectedOptions(selectedOptions: $selectedOptions) {
        ...ProductVariantFragment
      }
      mainMedia: metafields(identifiers: {key: "mainmedia", namespace: "custom"}) {
        key
        value
      }
      additionalMedia: metafields(identifiers: {key: "additionalmedia", namespace: "custom"}) {
        key
        value
      } 
      properties: metafields(identifiers: {key: "properties", namespace: "custom"}) {
        key
        value
      }
      items: metafields(identifiers: {key: "items", namespace: "custom"}) {
        key
        value
      }
      productdescription: metafields(identifiers: {key: "productdescription", namespace: "custom"}) {
        key
        value
      }  
      productproperty: metafield(key: "productproperty", namespace: "custom") {
        references(first: 10) {
          nodes {
            ...MetaObjectContentFragment
          }
        }
      }  
      productBom: metafield(key: "bom", namespace: "custom") {
        references(first: 10) {
          nodes {
            ...MetaObjectContentFragment
          }
        }
      }     
      media(first: 7) {
        nodes {
          ...Media
        }
      }
      variants(first: 1) {
        nodes {
          ...ProductVariantFragment
        }
      }
      seo {
        description
        title
      }
    }
    shop {
      name
      primaryDomain {
        url
      }
      shippingPolicy {
        body
        handle
      }
      refundPolicy {
        body
        handle
      }
    }
  }
`;

async function getRecommendedProducts(
  storefront: Storefront,
  collectionId: string,
  productId: string,
) {
  const products = await storefront.query<{
    collection: {products: ProductConnection};
  }>(COLLECTION_CONTENT_QUERY, {
    variables: {id: collectionId},
  });

  invariant(products, 'No data returned from Shopify API');
  const mergedProducts = _.get(
    products,
    'collection.products.nodes',
    [],
  )?.filter((it) => {
    return _.get(it, 'id') != productId;
  });
  return mergedProducts?.slice(0, 4);
}
